import { EventInput } from '@fullcalendar/core';
import { Item } from '../../form/select-field/select-field.component';
import { snapDateToMacchinarioSlot } from '../calendar.utils';

/**
 * Grid start/end hour: covers the whole day because events like travel time can
 * fall outside working hours. A narrower range would wrongly collapse those
 * events at the top of the grid (computeEventTop clamps to 0 for times before
 * START_HOUR), hiding some of them and showing others at a visually wrong
 * position/time.
 */
export const START_HOUR = 0;
export const END_HOUR = 24;
export const SLOT_HEIGHT_PX = 48;

/**
 * @return {{ hours: number; minutes: number }} time (from grid start) for a given 30-minute slot index.
 */
export function slotIndexToHM(slotIndex: number): {
  hours: number;
  minutes: number;
} {
  const totalMinutes = START_HOUR * 60 + slotIndex * 30;
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

/**
 * Applies a slot index's time onto a base date (same day).
 * @return {Date} `base`'s day with the slot's time applied.
 */
export function slotIndexToDate(slotIndex: number, base: Date): Date {
  const date = new Date(base);
  const { hours, minutes } = slotIndexToHM(slotIndex);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * @return {{ start: Date; end: Date }} midnight-to-midnight bounds of `day`.
 */
export function getDayBounds(day: Date): { start: Date; end: Date } {
  const start = new Date(day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { start, end };
}

/**
 * Computes an event's vertical (top) position in pixels, clamped to `dayStart`.
 * @return {number} the top offset in pixels.
 */
export function computeEventTop(event: EventInput, dayStart: Date): number {
  const start = new Date(event.start as string);
  const effectiveStart = start < dayStart ? dayStart : start;
  const minutesFromStart =
    (effectiveStart.getHours() - START_HOUR) * 60 + effectiveStart.getMinutes();
  return Math.max(0, (minutesFromStart / 30) * SLOT_HEIGHT_PX);
}

/**
 * Computes an event's height in pixels (24px minimum), clamped to `dayEnd`.
 * @return {number} the height in pixels.
 */
export function computeEventHeight(
  event: EventInput,
  dayStart: Date,
  dayEnd: Date
): number {
  const start = new Date(event.start as string);
  const end = new Date(event.end as string);
  const effectiveStart = start < dayStart ? dayStart : start;
  const effectiveEnd = end > dayEnd ? dayEnd : end;
  const durationMinutes =
    (effectiveEnd.getTime() - effectiveStart.getTime()) / 60000;
  const height = Math.max(24, (durationMinutes / 30) * SLOT_HEIGHT_PX);

  return height;
}

/**
 * @return {string} time label (e.g. "10:00") for a given slot index.
 */
export function computeSlotTimeLabel(slotIndex: number): string {
  const { hours, minutes } = slotIndexToHM(slotIndex);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Granularità di snap del drag & drop (riposizionamento di un evento
 * esistente): più fine dei 30' della griglia visiva (`SLOT_HEIGHT_PX`
 * rappresenta sempre uno slot da 30'), che restano invariati — stesso
 * concetto di `snapDuration` indipendente da `slotDuration` in FullCalendar
 * (v. calendar-options.builder.ts).
 */
export const DRAG_SNAP_MINUTES = 15;

/**
 * Converte una posizione Y relativa alla colonna in minuti-da-mezzanotte,
 * arrotondati per difetto a `DRAG_SNAP_MINUTES`, clampati all'interno della
 * griglia (`START_HOUR`-`END_HOUR`).
 * @return {number} minuti da mezzanotte, snappati.
 */
export function pixelsToSnappedMinutes(relativeY: number): number {
  const rawMinutes = START_HOUR * 60 + (relativeY / SLOT_HEIGHT_PX) * 30;
  const snapped = Math.floor(rawMinutes / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES;
  const maxMinutes = END_HOUR * 60 - DRAG_SNAP_MINUTES;
  return Math.max(START_HOUR * 60, Math.min(snapped, maxMinutes));
}

/**
 * Come `pixelsToSnappedMinutes`, ma quando la colonna ha un macchinario
 * attivo lo snap segue lo slot proprio del macchinario (`durataSlotMacchinario`,
 * ancorato all'inizio della sua fascia di disponibilità — v.
 * `snapDateToMacchinarioSlot`) invece del generico quarto d'ora: un
 * macchinario con slot da 20' deve proporre 08:00/08:20/08:40, non
 * 08:00/08:15/08:30/08:45. Torna `null` se il punto agganciato non cade in
 * nessuna fascia di disponibilità del macchinario (drop da bloccare), non un
 * fallback silenzioso al quarto d'ora.
 */
export function pixelsToMachinerySnappedMinutes(
  relativeY: number,
  macchinario: Item | null | undefined
): number | null {
  if (!macchinario?.durataSlotMacchinario || !macchinario?.orariAttivita?.length) {
    return pixelsToSnappedMinutes(relativeY);
  }
  const rawMinutes = START_HOUR * 60 + (relativeY / SLOT_HEIGHT_PX) * 30;
  const probe = new Date();
  probe.setHours(0, 0, 0, 0);
  probe.setMinutes(Math.round(rawMinutes));
  const snapped = snapDateToMacchinarioSlot(probe, macchinario);
  if (!snapped) return null;
  return snapped.getHours() * 60 + snapped.getMinutes();
}

/**
 * Applica dei minuti-da-mezzanotte su una data base (stesso giorno).
 * @return {Date} `base` con l'orario di `minutes` applicato.
 */
export function minutesToDate(minutes: number, base: Date): Date {
  const date = new Date(base);
  date.setHours(0, minutes, 0, 0);
  return date;
}

/**
 * Offset verticale (px) per un valore in minuti-da-mezzanotte, continuo (non
 * quantizzato allo slot da 30') — usato per posizionare il placeholder del
 * drag a metà slot quando si è snappato a un quarto d'ora.
 * @return {number} offset in px dall'inizio della griglia.
 */
export function minutesToTopPx(minutes: number): number {
  return ((minutes - START_HOUR * 60) / 30) * SLOT_HEIGHT_PX;
}

/**
 * @return {string} time label (e.g. "13:45") per un valore in
 * minuti-da-mezzanotte (non vincolato a un multiplo di 30, a differenza di
 * `computeSlotTimeLabel`).
 */
export function formatMinutesLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

/**
 * @return {{ label: string; isHour: boolean }[]} the grid's 30-minute time slots.
 */
export function buildTimeSlots(): { label: string; isHour: boolean }[] {
  const slots: { label: string; isHour: boolean }[] = [];
  for (let h = START_HOUR; h < END_HOUR; h++) {
    slots.push({
      label: `${h.toString().padStart(2, '0')}:00`,
      isHour: true,
    });
    slots.push({ label: '', isHour: false });
  }
  return slots;
}
